import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ErrorMessage, Field, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Col, Form, Row } from "react-bootstrap";
import { post } from "../../../helpers/api_helper";
import { Spin } from "antd";
import {
  successMessage,
  errorMessage,
  capitalizeString,
  warningMessage,
  delete_RecordWithoutApi,
} from "../CommonFunctions";
import {
  POST_GetInventoryGRNDetailByID,
  POST_GetVoucherSeriesDDLList,
  POST_GetSupplierDDLList,
  POST_GetInventoryItemsListDLL,
  POST_InventoryGRNInsert,
  POST_InventoryGRNUpdate,
} from "../../../helpers/url_helper";
import CustomSelect from "../../../CustomControls/CustomSelect";
import { SVGICON } from "../../../constant/theme";
import CustomInput from "../../../CustomControls/CustomInput";
import CustomFlatpickr from "../../../CustomControls/CustomFlatpickr";
import moment from "moment";

const AddInventoryGRN = () => {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const { PrimaryIdParam, ViewPrimaryIdParam } = useParams();
  const AuthUser = JSON.parse(localStorage.getItem("userDetails"));
  const [grnNoOptions, setGRNNoOptions] = useState([]);
  const [IsItemIssue, setIsItemIssue] = useState(false);
  const [calTotalAmount, setCalTotalAmount] = useState(false);
  const [getSupplierDDLList, setGetSupplierDDLList] = useState([]);
  const [getInventoryItemsListDLL, setGetInventoryItemsListDLL] = useState([]);
  useEffect(() => {
    getSupplierDDLListApi();
    getInventoryItemsListDLLApi();
    if (PrimaryIdParam || ViewPrimaryIdParam) {
      getInventoryGRNDetailByID();
    }
  }, []);

  const validationSchema = Yup.object().shape({
    GRNDate: Yup.string().trim().required("GRN Date is required field"),
    GRNNoID: Yup.mixed()
      .nullable()
      .test("GRNNoID-test", "GRN No is required field", function (value) {
        return value && value !== 0;
      }),
    SupplierID: Yup.string().trim().required("Supplier Name is required field"),
    Listuddt: Yup.array().test(
      "has-valid-item",
      "Please add at least one valid item.",
      (list) => {
        return Array.isArray(list) && list.some((x) => x?.IsDelete !== true);
      }
    ),
  });

  const formik = useFormik({
    validationSchema,
    onSubmit: (values, formikHelpers) =>
      handleValidSubmit(values, formikHelpers),
    initialValues: {
      GRNNo: "",
      GRNNoID: "",
      GRNDate: new Date(),
      PONo: "",
      PODate: "",
      GRNAmount: "",
      Remarks: "",
      SupplierID: "",
      SupplierName: "",
      CreatedByEncrypt: "",
      ModifiedByEncrypt: "",
      Listuddt: [],
      TotalAmount: "",
      UnitPrice: "",
      EditUnitPrice: "",
      Qty: "",
      AddUpdateQty: "",
      IsEdit: true,
      IsItemStockSerialNumberWise: false,
      InventoryItemStockIDEncrypted: "",
      actionFlag: 0, // 0 = add, 1 = edit
      indxGRN: -1,
      InventoryItemIDEncrypted: "",
      InventoryItem: "",
      IsConsumable: false, // Default value
      ConsumptionUnit: "",
      SerialNo: "",
    },
    validateOnBlur: false,
  });

  const {
    values: {
      Listuddt,
      actionFlag,
      GRNDate,
      InventoryItemIDEncrypted,
      InventoryItem,
      InventoryItemStockIDEncrypted,
      Qty,
      AddUpdateQty,
      UnitPrice,
      TotalAmount,
      indxGRN,
      IsItemStockSerialNumberWise,
      IsConsumable,
      EditUnitPrice,
      ConsumptionUnit,
      SerialNo,
    },
  } = formik;

  useEffect(() => {
    if (formik?.values?.Listuddt?.length > 0) {
      console.log("Listuddt");
      const total = (formik.values.Listuddt || [])
        .filter((x) => x.IsDelete === false)
        .reduce((sum, item) => sum + (parseFloat(item.TotalAmount) || 0), 0);
      formik.setFieldValue("GRNAmount", total);
    }
  }, [formik.values.Listuddt]);

  useEffect(() => {
    if (calTotalAmount === true) {
      const qty = parseFloat(formik.values.Qty);
      const price = parseFloat(formik.values.UnitPrice);

      if (!isNaN(qty) && !isNaN(price)) {
        const total = qty * price;
        formik.setFieldValue("TotalAmount", parseFloat(total.toFixed(2)));
        setCalTotalAmount(false);
      } else {
        formik.setFieldValue("TotalAmount", "");
        setCalTotalAmount(false);
      }
    }
  }, [formik.values.Qty, formik.values.UnitPrice]);

  const handleValidSubmit = (values) => {
    if (Listuddt?.filter((x) => x?.IsDelete === false).length === 0) {
      formik.setFieldError("Listuddt", "Item detail is required field.");
      formik.setFieldTouched("Listuddt", true);
      return;
    }

    if (
      Listuddt?.some(
        (x) =>
          x?.IsDelete === false &&
          x?.IsItemStockSerialNumberWise === true &&
          (!x?.SerialNo || x?.SerialNo?.trim() === "")
      )
    ) {
      formik.setFieldError(
        "Listuddt",
        "Please enter all serial numbers for the items in the list."
      );
      formik.setFieldTouched("Listuddt", true);
      return;
    }

    const serialNos = Listuddt?.filter(
      (x) => x?.IsDelete === false && x?.IsItemStockSerialNumberWise === true
    )?.map(
      (x) =>
        `${x.InventoryItemIDEncrypted?.trim().toLowerCase()}|${x.SerialNo?.trim().toLowerCase()}`
    );

    // Check for duplicates
    const hasDuplicates = new Set(serialNos).size !== serialNos.length;

    if (hasDuplicates) {
      formik.setFieldError(
        "Listuddt",
        "Duplicate Serial Numbers are not allowed."
      );
      formik.setFieldTouched("Listuddt", true);
      return;
    }

    setIsLoading(true);

    const payload = {
      // ...values,
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
      InventoryGRNIDEncrypted: PrimaryIdParam
        ? window.atob(PrimaryIdParam)
        : "",
      GRNNo: values?.GRNNoID,
      GRNDate: values?.GRNDate
        ? moment(values?.GRNDate).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      PONo: values?.PONo,
      PODate: values?.PODate
        ? moment(values?.PODate).format("YYYY-MM-DD").replace(/^\-/, "")
        : "",
      GRNAmount: values?.GRNAmount.toString(),
      Remarks: values?.Remarks,
      SupplierIDEncrypted: values?.SupplierID,
      CreatedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      ModifiedByEncrypt: AuthUser?.ReferenceIDEncrypt
        ? AuthUser?.ReferenceIDEncrypt
        : "0",
      Listuddt: values?.Listuddt?.map((item) => ({
        InventoryItemIDEncrypted: item?.InventoryItemIDEncrypted,
        InventoryItemStockIDEncrypted:
          item?.InventoryItemStockIDEncrypted ?? "",
        InventoryItem: item?.InventoryItem,
        SerialNumber: item?.SerialNo,
        Qty: item?.Qty.toString(),
        UnitPrice: item?.UnitPrice ? item?.UnitPrice.toString() : "",
        IsConsumable: item?.IsConsumable,
        IsItemStockSerialNumberWise: item?.IsItemStockSerialNumberWise,
        IsDelete: item?.IsDelete,
      })),
    };
    // return
    post(
      PrimaryIdParam ? POST_InventoryGRNUpdate : POST_InventoryGRNInsert,
      payload
    )
      .then((response) => {
        if (response?.IsSuccess) {
          successMessage(response?.Message);
          setTimeout(() => {
            navigate("/grn");
          }, 1000);
        } else {
          errorMessage(response?.Message);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setIsLoading(false);
      });
  };

  const getInventoryGRNDetailByID = () => {
    setIsLoading(true);
    post(POST_GetInventoryGRNDetailByID, {
      InventoryGRNIDEncrypted: PrimaryIdParam
        ? window.atob(PrimaryIdParam)
        : window.atob(ViewPrimaryIdParam),
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setIsItemIssue(response?.IsItemIssue ?? false);
          const listMapped = response?.List?.map((item) => ({
            InventoryItemIDEncrypted: item?.InventoryItemIDEncrypted,
            InventoryItemStockIDEncrypted:
              item?.InventoryItemStockIDEncrypted ?? "",
            InventoryItem: item?.ItemName,
            SerialNo: item?.SerialNumber,
            Qty: item?.TotalQty?.toString() ?? "0",
            ConsumptionUnit: item?.ConsumptionUnit ?? "",
            AvailableQty: item?.AvailableQty?.toString() ?? "0",
            UnitPrice: item?.UnitPrice ?? 0,
            IsEdit: item?.IsEdit ?? true,
            IsDiscontinued: item?.IsDiscontinued,
            Remarks: item?.Remarks ?? "",
            TotalUnitQty: item?.TotalUnitQty ?? 0,
            TotalAvailableUnitQty: item?.TotalAvailableUnitQty ?? 0,
            TotalAmount: item?.TotalAmount ?? 0,
            IsConsumable: item?.IsConsumable,
            IsItemStockSerialNumberWise: item?.IsItemStockSerialNumberWise,
            IsDelete: false,
          }));
          formik.setValues({
            ...response,
            GRNNo: response?.GRNNo,
            GRNNoID: response?.InventoryGRNIDEncrypted,
            GRNDate: ViewPrimaryIdParam
              ? response?.GRNDate
              : new Date(response?.GRNDate),
            PONo: response?.PONo,
            PODate: ViewPrimaryIdParam
              ? response?.PODate
              : response?.PODate
              ? new Date(response?.PODate)
              : "",
            GRNAmount: response?.GRNAmount.toString(),
            Remarks: response?.Remarks,
            SupplierID: response?.SupplierIDEncrypted,
            SupplierName: response?.SupplierName,
            CreatedByEncrypt: AuthUser?.ReferenceIDEncrypt
              ? AuthUser?.ReferenceIDEncrypt
              : "0",
            ModifiedByEncrypt: AuthUser?.ReferenceIDEncrypt
              ? AuthUser?.ReferenceIDEncrypt
              : "0",
            Listuddt: listMapped,
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const handleAddGRNDetail = () => {
    let itemExists = false;
    const list = formik?.values?.Listuddt || [];
    let isValidationRequired = false;

    if (!InventoryItemIDEncrypted) {
      formik.setFieldError("InventoryItemIDEncrypted", "Item is required");
      formik.setFieldTouched("InventoryItemIDEncrypted", true);
      isValidationRequired = true;
    }

    if (!Qty || isNaN(Qty) || parseInt(Qty) <= 0) {
      formik.setFieldError("Qty", "Qty is required");
      formik.setFieldTouched("Qty", true);
      isValidationRequired = true;
    }

    const isDuplicate = Listuddt.some((item, index) => {
      if (item.IsDelete) return false; // Ignore deleted rows
      if (actionFlag === 1 && index === indxGRN) return false; // Skip current item in edit mode

      if (IsItemStockSerialNumberWise) {
        // Serial number-wise check
        return (
          item.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
          item.SerialNo === SerialNo
        );
      } else {
        // Normalize values — treat "", null, undefined as 0
        const itemUnitPrice =
          item.UnitPrice === "" ||
          item.UnitPrice === null ||
          item.UnitPrice === undefined
            ? 0
            : parseFloat(item.UnitPrice);

        const inputUnitPrice =
          UnitPrice === "" || UnitPrice === null || UnitPrice === undefined
            ? 0
            : parseFloat(UnitPrice);

        return (
          item.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
          itemUnitPrice === inputUnitPrice
        );
      }
    });

    if (isDuplicate) {
      if (IsItemStockSerialNumberWise) {
        formik.setFieldError(
          "InventoryItemStockIDEncrypted",
          "Item with this Serial No already added"
        );
        formik.setFieldTouched("InventoryItemStockIDEncrypted", true);
        isValidationRequired = true;
      } else {
        formik.setFieldError(
          "InventoryItemIDEncrypted",
          "Item already exists with the same price"
        );
        formik.setFieldTouched("InventoryItemIDEncrypted", true);
        isValidationRequired = true;
      }
      return;
    }

    if (isValidationRequired) {
      return;
    }
    let ListuddtLength = Listuddt?.filter(
      (x) =>
        x?.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
        x?.IsDelete === false
    ).length;

    if (ListuddtLength > 0) {
      if (IsItemStockSerialNumberWise) {
        if (actionFlag === 1) {
          if (parseInt(Qty) > parseInt(AddUpdateQty)) {
            let editList = Listuddt?.map((x) => {
              if (
                x.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
                x.InventoryItemStockIDEncrypted ==
                  InventoryItemStockIDEncrypted &&
                x?.UnitPrice === EditUnitPrice
              ) {
                return {
                  ...x,
                  // Qty: Qty,
                  UnitPrice: UnitPrice,
                  TotalAmount: UnitPrice,
                };
              } else {
                return { ...x };
              }
            });

            const mainQty = parseInt(Qty) - parseInt(AddUpdateQty);
            const newRows = [];
            const count = parseInt(mainQty);

            for (let i = 0; i < count; i++) {
              newRows.push({
                InventoryItemIDEncrypted,
                InventoryItemStockIDEncrypted: "",
                InventoryItem,
                SerialNo: "",
                Qty: 1,
                IsEdit: true,
                ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
                UnitPrice: UnitPrice,
                IsConsumable: IsConsumable,
                TotalAmount: UnitPrice,
                IsItemStockSerialNumberWise,
                IsDelete: false,
              });
            }

            formik.setFieldValue("Listuddt", [...editList, ...newRows]);
          } else if (parseInt(Qty) < parseInt(AddUpdateQty)) {
            const qtyInt = parseInt(Qty);
            const addQtyInt = parseInt(AddUpdateQty);
            const deleteCount = addQtyInt - qtyInt;

            const unitPriceSafe = parseFloat(UnitPrice) || "";

            // Find all matching and active rows
            let matchedRows = Listuddt?.filter(
              (x) =>
                x.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
                x.InventoryItemStockIDEncrypted ===
                  InventoryItemStockIDEncrypted &&
                x.UnitPrice === EditUnitPrice &&
                x.IsDelete === false
            );

            // Get indexes of last `deleteCount` matching rows
            const indexesToDelete = matchedRows
              .slice(-deleteCount)
              .map((row) => Listuddt.indexOf(row));

            // Update list
            const editList = Listuddt.map((x, idx) => {
              if (indexesToDelete.includes(idx)) {
                return { ...x, IsDelete: true };
              }

              if (
                x.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
                x.InventoryItemStockIDEncrypted ===
                  InventoryItemStockIDEncrypted &&
                x.UnitPrice === EditUnitPrice &&
                x.IsDelete === false
              ) {
                return {
                  ...x,
                  UnitPrice: unitPriceSafe,
                  TotalAmount: unitPriceSafe,
                };
              }

              return x;
            });

            formik.setFieldValue("Listuddt", editList);
          } else {
            if (InventoryItemStockIDEncrypted) {
              formik.setFieldValue(
                "Listuddt",
                Listuddt?.map((x, idx) => {
                  if (
                    x.InventoryItemStockIDEncrypted ===
                      InventoryItemStockIDEncrypted &&
                    x?.UnitPrice === EditUnitPrice
                  ) {
                    return {
                      ...x,
                      UnitPrice: UnitPrice,
                      TotalAmount: UnitPrice,
                    };
                  } else {
                    return { ...x };
                  }
                })
              );
            } else {
              formik.setFieldValue(
                "Listuddt",
                Listuddt?.map((x, idx) => {
                  // changes
                  if (
                    x?.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
                    x.InventoryItemStockIDEncrypted ===
                      InventoryItemStockIDEncrypted &&
                    x?.UnitPrice === EditUnitPrice
                  ) {
                    return {
                      ...x,
                      UnitPrice: UnitPrice,
                      TotalAmount: UnitPrice,
                    };
                  } else {
                    return { ...x };
                  }
                })
              );
            }
          }
        } else {
          // Add: generate N rows with Qty=1 and unique SerialNo
          const newRows = [];
          const count = parseInt(Qty);

          for (let i = 0; i < count; i++) {
            newRows.push({
              InventoryItemIDEncrypted,
              InventoryItemStockIDEncrypted: "",
              InventoryItem,
              SerialNo: "",
              Qty: 1,
              IsEdit: true,
              ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
              UnitPrice,
              TotalAmount: UnitPrice,
              IsConsumable,
              IsItemStockSerialNumberWise,
              IsDelete: false,
            });
          }
          formik.setFieldValue("Listuddt", [...list, ...newRows]);
        }
      } else {
        if (actionFlag === 1) {
          formik.setFieldValue(
            "Listuddt",
            Listuddt?.map((x, idx) => {
              if (
                x.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
                x?.UnitPrice === EditUnitPrice
              ) {
                return {
                  ...x,
                  Qty: Qty,
                  ConsumptionUnit: ConsumptionUnit,
                  UnitPrice: UnitPrice,
                  TotalAmount: TotalAmount,
                };
              } else {
                return { ...x };
              }
            })
          );
        } else {
          const newItem = {
            InventoryItemIDEncrypted,
            InventoryItemStockIDEncrypted,
            InventoryItem,
            Qty,
            ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
            UnitPrice,
            IsEdit: true,
            SerialNo: "", // Add SerialNo field
            TotalAmount,
            IsConsumable,
            IsItemStockSerialNumberWise,
            IsDelete: false,
          };
          formik.setFieldValue("Listuddt", [...list, newItem]);
        }
      }
    } else {
      // SERIAL NUMBER WISE LOGIC
      if (IsItemStockSerialNumberWise) {
        if (actionFlag === 1 && indxGRN > -1) {
          // Edit: update only the selected row, keep SerialNo as is
          const updatedList = list.map((item, i) =>
            i === indxGRN
              ? {
                  ...item,
                  InventoryItemIDEncrypted,
                  InventoryItemStockIDEncrypted,
                  InventoryItem,
                  SerialNo: "",
                  Qty: 1,
                  IsEdit: true,
                  ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
                  UnitPrice,
                  TotalAmount: UnitPrice,
                  IsConsumable,
                  IsItemStockSerialNumberWise,
                  IsDelete: false,
                }
              : item
          );
          formik.setFieldValue("Listuddt", updatedList);
        } else {
          // Add: generate N rows with Qty=1 and unique SerialNo
          const newRows = [];
          const count = parseInt(Qty);

          for (let i = 0; i < count; i++) {
            newRows.push({
              InventoryItemIDEncrypted,
              InventoryItemStockIDEncrypted: "",
              InventoryItem,
              SerialNo: "",
              Qty: 1,
              IsEdit: true,
              ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
              UnitPrice,
              TotalAmount: UnitPrice,
              IsConsumable,
              IsItemStockSerialNumberWise,
              IsDelete: false,
            });
          }
          formik.setFieldValue("Listuddt", [...list, ...newRows]);
        }
      } else {
        // // NORMAL LOGIC (single row, Qty as entered)
        // itemExists = list.some(
        //   (item, i) =>
        //     i !== indxGRN &&
        //     item.InventoryItemIDEncrypted === InventoryItemIDEncrypted &&
        //     item.Qty === Qty &&
        //     item.UnitPrice === UnitPrice
        // );
        // if (itemExists) {
        //   warningMessage("GRN Detail entry already exists.");
        //   return;
        // }
        if (actionFlag === 1 && indxGRN > -1) {
          const updatedList = list.map((item, i) =>
            i === indxGRN
              ? {
                  ...item,
                  InventoryItemIDEncrypted,
                  InventoryItemStockIDEncrypted,
                  InventoryItem,
                  Qty,
                  IsEdit: true,
                  ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
                  UnitPrice,
                  SerialNo: "",
                  TotalAmount,
                  IsConsumable,
                  IsItemStockSerialNumberWise,
                  IsDelete: false,
                }
              : item
          );
          formik.setFieldValue("Listuddt", updatedList);
        } else {
          const newItem = {
            InventoryItemIDEncrypted,
            InventoryItemStockIDEncrypted: "",
            InventoryItem,
            Qty,
            IsEdit: true,
            ConsumptionUnit: ConsumptionUnit ? ConsumptionUnit : "",
            UnitPrice,
            SerialNo: "", // Add SerialNo field
            TotalAmount,
            IsConsumable,
            IsItemStockSerialNumberWise,
            IsDelete: false,
          };
          formik.setFieldValue("Listuddt", [...list, newItem]);
        }
      }
    }
    // Clear form fields
    formik.setFieldValue("InventoryItemIDEncrypted", "");
    formik.setFieldValue("InventoryItemStockIDEncrypted", "");
    formik.setFieldValue("InventoryItem", "");
    formik.setFieldValue("Qty", "");
    formik.setFieldValue("ConsumptionUnit", "");
    formik.setFieldValue("UnitPrice", "");
    formik.setFieldValue("TotalAmount", "");
    formik.setFieldValue("indxGRN", "");
    formik.setFieldValue("actionFlag", 0);
  };

  const deleteItemDetailList = (i) => {
    if (Listuddt[i]?.InventoryItemIDEncrypted) {
      // alert(1)
      formik.setFieldValue(
        "Listuddt",
        Listuddt?.map((x, idx) => {
          if (idx == i) {
            return {
              ...x,
              IsDelete: true,
            };
          } else {
            return { ...x };
          }
        })
      );
    } else {
      formik.setFieldValue(
        "Listuddt",
        Listuddt?.filter((x, idx) => idx != i)
      );
    }
    formik.setFieldValue("InventoryItemIDEncrypted", "");
    formik.setFieldValue("InventoryItem", "");
    formik.setFieldValue("Qty", "");
    formik.setFieldValue("ConsumptionUnit", "");
    formik.setFieldValue("UnitPrice", "");
    formik.setFieldValue("TotalAmount", "");
    formik.setFieldValue("indxGRN", "");
    formik.setFieldValue("actionFlag", 0); // Reset to add mode
  };

  const clearItemDetailList = () => {
    formik.setFieldValue("InventoryItemIDEncrypted", "");
    formik.setFieldValue("InventoryItem", "");
    formik.setFieldValue("Qty", "");
    formik.setFieldValue("ConsumptionUnit", "");
    formik.setFieldValue("UnitPrice", "");
    formik.setFieldValue("TotalAmount", "");
    formik.setFieldValue("indxGRN", "");
    formik.setFieldValue("actionFlag", 0); // Reset to add mode
  };

  const editItemDetailList = (i, IsItemStockSerialNumberWise) => {
    let editData = Listuddt?.[i];

    formik.setFieldValue("IsConsumable", editData?.IsConsumable);
    formik.setFieldValue("InventoryItem", editData?.InventoryItem);
    formik.setFieldValue(
      "InventoryItemIDEncrypted",
      editData?.InventoryItemIDEncrypted
    );

    formik.setFieldValue(
      "UnitPrice",
      editData?.UnitPrice ? parseFloat(editData?.UnitPrice) : ""
    );
    formik.setFieldValue(
      "TotalAmount",
      editData?.TotalAmount ? parseFloat(editData?.TotalAmount) : ""
    );
    formik.setFieldValue("indxGRN", i);
    formik.setFieldValue("actionFlag", 1); // 1 = edit
    formik.setFieldValue(
      "IsItemStockSerialNumberWise",
      IsItemStockSerialNumberWise
    );

    const isStockWise = !!editData?.InventoryItemStockIDEncrypted;

    const ListuddtLength = (Listuddt || []).filter(
      (x) =>
        x?.InventoryItemIDEncrypted === editData?.InventoryItemIDEncrypted &&
        x?.InventoryItemStockIDEncrypted ===
          editData?.InventoryItemStockIDEncrypted &&
        x?.UnitPrice === editData?.UnitPrice &&
        x?.IsDelete === false
    ).length;

    const qtyValue = isStockWise
      ? IsItemStockSerialNumberWise
        ? 1
        : editData?.Qty
      : IsItemStockSerialNumberWise
      ? ListuddtLength
      : editData?.Qty;

    formik.setFieldValue("Qty", qtyValue);
    formik.setFieldValue(
      "ConsumptionUnit",
      editData?.ConsumptionUnit ? editData?.ConsumptionUnit : ""
    );
    formik.setFieldValue("AddUpdateQty", qtyValue);
    formik.setFieldValue("EditUnitPrice", editData?.UnitPrice);
    formik.setFieldValue(
      "InventoryItemStockIDEncrypted",
      editData?.InventoryItemStockIDEncrypted
    );
  };

  useEffect(() => {
    if (!GRNDate) {
      formik.setFieldValue("GRNDate", "");
    }
    getGRNNoDDL();
  }, [GRNDate]);

  const getGRNNoDDL = async () => {
    setIsLoading(true);
    await post(POST_GetVoucherSeriesDDLList, {
      TranType: 19, // 19 = GRN
      TranDate: formik?.values?.GRNDate
        ? moment(formik?.values?.GRNDate)
            .format("YYYY-MM-DD")
            .replace(/^\-/, "")
        : "",
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setGRNNoOptions(
            response?.VoucherSeriesList?.map((item) => ({
              label: item.VoucherSeries,
              value: item.VoucherSeriesIDEncrypt,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getSupplierDDLListApi = async () => {
    setIsLoading(true);
    await post(POST_GetSupplierDDLList, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setGetSupplierDDLList(
            response?.List?.map((item) => ({
              label: item.SupplierName,
              value: item.SupplierIDEncrypt,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  const getInventoryItemsListDLLApi = async () => {
    setIsLoading(true);
    await post(POST_GetInventoryItemsListDLL, {
      CompanyIDEncrypted: AuthUser?.CompanyIDEncrypt,
      BranchIDEncrypted: AuthUser?.BranchIDEncrypt,
    })
      .then((response) => {
        if (response?.IsSuccess) {
          setGetInventoryItemsListDLL(
            response?.List?.map((item) => ({
              label: item.ItemName,
              value: item.InventoryItemIDEncrypted,
              IsItemStockSerialNumberWise: item.IsItemStockSerialNumberWise,
              IsConsumable: item.IsConsumable,
              ConsumptionUnit: item.ConsumptionUnit,
            }))
          );
        }
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setIsLoading(false);
      });
  };

  return (
    <>
      <div className="card m-1">
        <div className="card-body p-1">
          <Spin
            size="large"
            spinning={isLoading}
            tip={"Loading..."}
            style={{ position: "fixed" }}
          >
            <div className="tbl-caption p-2">
              <h4 className="heading mb-0">
                {PrimaryIdParam ? "Edit" : ViewPrimaryIdParam ? "View" : "Add"}{" "}
                Goods Receipt Note
              </h4>
            </div>
            <hr className="mt-0 mb-1"></hr>
            <div className="p-2 pt-0">
              <FormikProvider value={formik}>
                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col lg={3}>
                      {!ViewPrimaryIdParam ? (
                        <Field
                          label="GRN Date"
                          name="GRNDate"
                          placeholder={"Select GRN Date"}
                          component={CustomFlatpickr}
                          options={{
                            altInput: true,
                            altFormat: "j-M-Y",
                            dateFormat: "d-m-Y",
                          }}
                          onChange={(date) => {
                            formik.setFieldValue("GRNDate", date);
                            formik.setFieldValue("PODate", "");
                          }}
                          required
                        />
                      ) : (
                        <Field
                          type="text"
                          label="GRN Date"
                          name="GRNDate"
                          placeholder={`Enter GRN Date`}
                          component={CustomInput}
                          maxLength={250}
                          autoFocus
                          disabled={ViewPrimaryIdParam ? true : false}
                        />
                      )}
                    </Col>
                    <Col lg={3}>
                      {PrimaryIdParam || ViewPrimaryIdParam ? (
                        <Field
                          type="text"
                          label="GRN No"
                          name="GRNNo"
                          placeholder={"GRN No"}
                          component={CustomInput}
                          maxLength={250}
                          autoFocus
                          onChange={(e) => {
                            formik.setFieldValue("GRNNo", e?.label ?? "");
                          }}
                          readOnly
                          disabled={true}
                        />
                      ) : (
                        <Field
                          label="GRN No"
                          name="GRNNoID"
                          placeholder={"GRN No"}
                          options={grnNoOptions}
                          component={CustomSelect}
                          isClearable={false}
                          onChange={(e) => {
                            formik.setFieldValue("GRNNo", e?.label ?? "");
                            formik.setFieldValue("GRNNoID", e?.value ?? 0);
                          }}
                          required
                        />
                      )}
                    </Col>
                    <Col lg={3}>
                      <Field
                        type="text"
                        label="PO No"
                        name="PONo"
                        placeholder={`Enter PO No`}
                        component={CustomInput}
                        maxLength={250}
                        autoFocus
                        onChange={(e) => {
                          formik.setFieldValue("PONo", capitalizeString(e));
                        }}
                        disabled={ViewPrimaryIdParam ? true : false}
                      />
                    </Col>
                    <Col lg={3}>
                      {!ViewPrimaryIdParam ? (
                        <Field
                          label="PO Date"
                          name="PODate"
                          placeholder={"Select PO Date"}
                          component={CustomFlatpickr}
                          options={{
                            altInput: true,
                            altFormat: "j-M-Y",
                            dateFormat: "d-m-Y",
                            maxDate: formik?.values?.GRNDate || null,
                          }}
                        />
                      ) : (
                        <Field
                          type="text"
                          label="PO Date"
                          name={"PODate"}
                          placeholder={`Enter PO Date`}
                          component={CustomInput}
                          maxLength={250}
                          autoFocus
                          disabled={ViewPrimaryIdParam ? true : false}
                        />
                      )}
                    </Col>
                    <Col lg={3}>
                      <Field
                        label="Supplier"
                        name="SupplierID"
                        placeholder={"Supplier"}
                        options={getSupplierDDLList}
                        component={CustomSelect}
                        isClearable={false}
                        onChange={(e) => {
                          formik.setFieldValue("SupplierName", e?.label ?? "");
                          formik.setFieldValue("SupplierID", e?.value ?? "");
                        }}
                        required
                        isDisabled={ViewPrimaryIdParam ? true : false}
                      />
                    </Col>
                  </Row>

                  <div
                    className="card-header"
                    style={{ padding: ".6rem 0rem" }}
                  >
                    <h4 className="card-title">Item Detail</h4>
                  </div>
                  <Row>
                    {!ViewPrimaryIdParam && (
                      <Col lg={12}>
                        <Row style={{ marginTop: "10px" }}>
                          <Col lg={3}>
                            <Field
                              label="Item"
                              name="InventoryItemIDEncrypted"
                              placeholder={"Select Item"}
                              options={getInventoryItemsListDLL}
                              component={CustomSelect}
                              isClearable={false}
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "InventoryItem",
                                  e?.label ?? ""
                                );
                                formik.setFieldValue(
                                  "InventoryItemIDEncrypted",
                                  e?.value ?? 0
                                );
                                formik.setFieldValue(
                                  "IsItemStockSerialNumberWise",
                                  e?.IsItemStockSerialNumberWise
                                );
                                formik.setFieldValue(
                                  "IsConsumable",
                                  e?.IsConsumable
                                );
                                formik.setFieldValue(
                                  "ConsumptionUnit",
                                  e?.ConsumptionUnit
                                );
                                formik.setFieldValue("Qty", "");
                                formik.setFieldValue("UnitPrice", "");
                                formik.setFieldValue("TotalAmount", "");
                                formik.setFieldValue("indxGRN", "");
                                formik.setFieldValue("actionFlag", 0);
                              }}
                              required
                              isDisabled={actionFlag == 1 ? true : false}
                            />
                          </Col>

                          <Col lg={2}>
                            <Field
                              type="text"
                              label={`Qty${
                                ConsumptionUnit ? ` (${ConsumptionUnit})` : ""
                              }`}
                              name="Qty"
                              placeholder={`Enter Qty`}
                              component={CustomInput}
                              maxLength={7}
                              required
                              onChange={(e) => {
                                const value = e.target.value;
                                if (/^\d*$/.test(value)) {
                                  formik.setFieldValue("Qty", value);
                                  setCalTotalAmount(true);
                                }
                              }}
                            />
                          </Col>

                          <Col lg={2}>
                            <Field
                              type="number"
                              label="Unit Price"
                              name="UnitPrice"
                              placeholder={`Enter Unit Price`}
                              component={CustomInput}
                              maxLength={100}
                              // required
                              onChange={(e) => {
                                const val = e.target.value;
                                formik.setFieldValue("UnitPrice", val);
                                setCalTotalAmount(true);
                                // Allow only 0-9
                                // if (/^[0-9]*$/.test(val)) {
                                // }
                              }}
                            />
                          </Col>

                          <Col lg={2}>
                            <Field
                              type="number"
                              label="Total Amount"
                              name="TotalAmount"
                              placeholder={`Enter Total Amount`}
                              component={CustomInput}
                              maxLength={100}
                              readOnly
                              onChange={(e) => {
                                formik.setFieldValue(
                                  "TotalAmount",
                                  e.target.value
                                );
                              }}
                            />
                          </Col>

                          <Col lg={2}>
                            <label style={{ marginTop: "22px" }}>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm"
                                onClick={(e) => {
                                  handleAddGRNDetail();
                                }}
                              >
                                {actionFlag == 1 ? "Update" : "Add"}
                              </button>
                            </label>
                            {actionFlag == 1 && (
                              <label
                                style={{ marginTop: "22px", marginLeft: 10 }}
                              >
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={(e) => {
                                    clearItemDetailList();
                                  }}
                                >
                                  {"Cancel"}
                                </button>
                              </label>
                            )}
                          </Col>
                        </Row>
                      </Col>
                    )}
                    <Col lg={12}>
                      <div
                        className="card-body"
                        style={{ paddingLeft: 0, paddingRight: 0 }}
                      >
                        <div className="table-responsive active-projects style-1 shorting">
                          <div
                            id="ItemDetail-tbl_wrapper"
                            className="dataTables_wrapper table-responsive no-footer "
                            style={{ overflowY: "auto" }}
                            //   style={{ height: "440px", overflowY: "auto" }}
                          >
                            <table
                              id="ItemDetail-tbl"
                              className="table dataTable no-footer mb-0"
                            >
                              <thead className="fixedheader">
                                <tr>
                                  {!ViewPrimaryIdParam && <th width="3%"></th>}
                                  <th width="20%">Item</th>
                                  <th width="20%">Serial No</th>
                                  <th width="20%">Qty</th>
                                  <th width="20%">Unit Name</th>
                                  <th width="20%">Unit Price</th>
                                  <th width="17%" style={{ textAlign: "left" }}>
                                    Total Amount
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {Listuddt?.filter((x) => x.IsDelete === false)
                                  .length > 0 ? (
                                  Listuddt?.map((item, indx) => (
                                    <tr
                                      key={indx}
                                      style={{
                                        display:
                                          item.IsDelete === true ? "none" : "",
                                      }}
                                    >
                                      {!ViewPrimaryIdParam && (
                                        <td style={{ textAlign: "center" }}>
                                          {item?.IsEdit == true && (
                                            <>
                                              <Link
                                                to="#"
                                                onClick={() => {
                                                  editItemDetailList(
                                                    indx,
                                                    item?.IsItemStockSerialNumberWise
                                                  );
                                                }}
                                                className="icon-box icon-box-md me-1"
                                                title="Edit"
                                              >
                                                {SVGICON.EditPrimary}
                                              </Link>

                                              <Link
                                                to="#"
                                                className="icon-box icon-box-md me-1"
                                                title="Delete"
                                                onClick={() => {
                                                  delete_RecordWithoutApi(
                                                    deleteItemDetailList,
                                                    indx
                                                  );
                                                }}
                                              >
                                                {SVGICON.DeleteDanger}
                                              </Link>
                                            </>
                                          )}
                                        </td>
                                      )}
                                      <td>{item.InventoryItem}</td>
                                      <td>
                                        {item?.IsItemStockSerialNumberWise ===
                                          true && (
                                          <Field
                                            type="text"
                                            name={`Listuddt.[${indx}].SerialNo`}
                                            component={CustomInput}
                                            placeholder="Serial No"
                                            noMarginBottom={true}
                                            style={{ width: "250px" }}
                                            required
                                            disabled={
                                              item?.InventoryItemStockIDEncrypted
                                                ? true
                                                : false
                                            }
                                          />
                                        )}
                                      </td>
                                      <td>{item.Qty}</td>
                                      <td>{item.ConsumptionUnit}</td>
                                      <td>{item.UnitPrice}</td>
                                      <td style={{ textAlign: "left" }}>
                                        {item.TotalAmount}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan={7}
                                      style={{ textAlign: "left" }}
                                    >
                                      <div>{"No Record Found..."}</div>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                        <ErrorMessage
                          name={"Listuddt"}
                          component="div"
                          className="invalid-feedback animated fadeInUp"
                          style={{ display: "block" }}
                        />
                      </div>
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={3} style={{ marginTop: "6px" }}>
                      <Field
                        type="text"
                        label="GRN Amount"
                        name="GRNAmount"
                        value={
                          formik.values.GRNAmount
                            ? parseFloat(formik.values.GRNAmount).toFixed(2)
                            : formik.values.GRNAmount
                        }
                        placeholder={`Enter GRN Amount`}
                        component={CustomInput}
                        maxLength={250}
                        autoFocus
                        onChange={(e) => {
                          formik.setFieldValue(
                            "GRNAmount",
                            capitalizeString(e)
                          );
                        }}
                        disabled={true}
                        readOnly
                      />
                    </Col>
                    <Col lg={3} style={{ marginTop: "6px" }}>
                      <Field
                        type="text"
                        label="Remarks"
                        name="Remarks"
                        placeholder={`Enter Remarks`}
                        component={CustomInput}
                        maxLength={500}
                        onChange={(e) => {
                          formik.setFieldValue("Remarks", e.target.value);
                        }}
                        disabled={ViewPrimaryIdParam ? true : false}
                      />
                    </Col>
                  </Row>
                  <hr className="mt-1 mb-2"></hr>
                  {!ViewPrimaryIdParam && (
                    <button
                      type="submit"
                      className="btn btn-primary btn-sm me-2"
                    >
                      <i className="fa-solid fa-floppy-disk"></i> Submit
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={(e) => {
                      navigate(-1);
                    }}
                  >
                    <i className="fa fa-cancel"></i> Cancel
                  </button>
                </Form>
              </FormikProvider>
            </div>
          </Spin>
        </div>
      </div>
    </>
  );
};

export default AddInventoryGRN;
